import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type OnboardingCompletionModalProps = {
  isOpen: boolean;
  onOpenDashboard: () => void;
  onShareAnyHomes: () => void;
  onClose: () => void;
  markCelebrationSeen: () => Promise<void> | void;
};

export default function OnboardingCompletionModal({
  isOpen,
  onOpenDashboard,
  onShareAnyHomes,
  onClose,
  markCelebrationSeen,
}: OnboardingCompletionModalProps) {
  const { t } = useTranslation();
  const [isClosing, setIsClosing] = useState(false);
  const [showParticles, setShowParticles] = useState(false);

  const title = useMemo(() => t('onboarding.completion.title'), [t]);

  useEffect(() => {
    if (!isOpen) return;

    setIsClosing(false);
    setShowParticles(true);

    return () => setShowParticles(false);
  }, [isOpen]);

  const finalizeAction = async (callback: () => void) => {
    if (isClosing) return;
    setIsClosing(true);
    try {
      await markCelebrationSeen();
    } finally {
      callback();
    }
  };

  const handleContinue = () => finalizeAction(onClose);
  const handleDashboard = () => finalizeAction(onOpenDashboard);
  const handleShare = () => finalizeAction(onShareAnyHomes);
  const handleClose = () => finalizeAction(onClose);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-completion-title"
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/60 px-4"
    >
      <div className="relative w-full max-w-xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <button
          type="button"
          aria-label={t('onboarding.completion.closeAriaLabel')}
          onClick={handleClose}
          disabled={isClosing}
          className="absolute right-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
        >
          ✕
        </button>

        <div className="relative px-6 pb-6 pt-8 sm:px-8 sm:pb-8 sm:pt-10">
          {showParticles && (
            <div
              className="pointer-events-none absolute inset-0 overflow-hidden"
              aria-hidden="true"
            >
              <span className="absolute left-[14%] top-[16%] h-2.5 w-2.5 rounded-full bg-emerald-400 animate-bounce" />
              <span className="absolute left-[26%] top-[10%] h-2 w-2 rounded-full bg-violet-400 animate-pulse" />
              <span className="absolute right-[18%] top-[14%] h-3 w-3 rounded-full bg-sky-400 animate-bounce" />
              <span className="absolute right-[28%] top-[22%] h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
              <span className="absolute left-1/2 top-[8%] h-2.5 w-2.5 -translate-x-1/2 rounded-full bg-emerald-500 animate-ping" />
            </div>
          )}

          <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500 text-xl font-semibold text-white">
              ✓
            </div>
          </div>

          <div className="text-center">
            <p className="mb-3 inline-flex rounded-full bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-700">
              {t('onboarding.completion.percentBadge')}
            </p>

            <h2
              id="onboarding-completion-title"
              className="mb-3 text-2xl font-semibold tracking-tight text-slate-900"
            >
              {title}
            </h2>

            <p className="mx-auto max-w-md text-sm leading-6 text-slate-600 sm:text-base">
              {t('onboarding.completion.description')}
            </p>
          </div>

          <div className="mt-8 space-y-3 flex flex-col items-center">
            {/* CTA principal */}
            <button
              type="button"
              onClick={handleContinue}
              disabled={isClosing}
              className="flex w-1/2 items-center justify-center rounded-2xl bg-slate-900 px-5 py-3 text-sm font-medium text-white transition hover:bg-slate-800 disabled:opacity-50"
            >
              {t('onboarding.completion.continue')}
            </button>

            {/* CTA secondaire */}
            <button
              type="button"
              onClick={handleDashboard}
              disabled={isClosing}
              className="flex w-1/2 items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
            >
              {t('onboarding.completion.openDashboard')}
            </button>

            {/* CTA tertiaire */}
            <button
              type="button"
              onClick={handleShare}
              disabled={isClosing}
              className="flex w-1/2 items-center justify-center px-5 py-2 text-sm text-slate-500 transition hover:text-slate-700 disabled:opacity-50"
            >
              {t('onboarding.completion.share')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
